import React from 'react';
import {
  Box,
  Paper,
  Grid,
  Button,
  TextField,
  Autocomplete,
  Typography,
} from 'components/mui';
import { DatePicker, LocalizationProvider, AdapterDateFns } from 'components/mui';
import { format, isValid } from 'date-fns';
import { REPORT_TYPES } from 'containers/pages/payrollReport/data';
import { PayrollParams } from 'containers/pages/payrollReport/types';
import { getMonthRange } from 'containers/pages/payrollReport/libs/dateUtils';
import { ConsultantOption } from 'api/cases/searchConsultants';

interface PayrollFiltersProps {
  params: PayrollParams;
  onParamsChange: (updates: Partial<PayrollParams>) => void;
  isAdmin: boolean;
  consultantOptions: ConsultantOption[];
  consultantLoading: boolean;
  onConsultantSearch: (keyword: string) => void;
  onConsultantSelect: (event: unknown, value: ConsultantOption | null) => void;
}

function PayrollFilters({
  params,
  onParamsChange,
  isAdmin,
  consultantOptions,
  consultantLoading,
  onConsultantSearch,
  onConsultantSelect,
}: PayrollFiltersProps) {
  const handleCurrentMonth = () => {
    const { firstDay, lastDay } = getMonthRange(0);
    onParamsChange({ StartDate: firstDay, EndDate: lastDay });
  };

  const handleLastMonth = () => {
    const { firstDay, lastDay } = getMonthRange(1);
    onParamsChange({ StartDate: firstDay, EndDate: lastDay });
  };

  const handleStartDateChange = (date: Date | null) => {
    if (date && isValid(date)) {
      onParamsChange({ StartDate: format(date, 'yyyy-MM-dd') });
    } else {
      onParamsChange({ StartDate: null });
    }
  };

  const handleEndDateChange = (date: Date | null) => {
    if (date && isValid(date)) {
      onParamsChange({ EndDate: format(date, 'yyyy-MM-dd') });
    } else {
      onParamsChange({ EndDate: null });
    }
  };

  const handleReportTypeChange = (
    _event: unknown,
    value: { Id: string; Title: string } | null,
  ) => {
    onParamsChange({ ReportType: (value?.Id as PayrollParams['ReportType']) || '' });
  };

  const selectedReportType =
    REPORT_TYPES.find((rt) => rt.Id === params.ReportType) || null;

  const selectedConsultant: ConsultantOption | null = params.ConsultantId
    ? { Id: Number(params.ConsultantId), FullName: params.ConsultantName || '' }
    : null;

  return (
    <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Payroll Report
      </Typography>

      {/* Date template buttons */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Button variant="outlined" size="small" onClick={handleCurrentMonth}>
          Current Month
        </Button>
        <Button variant="outlined" size="small" onClick={handleLastMonth}>
          Last Month
        </Button>
      </Box>

      {/* Date pickers */}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Grid container spacing={2} sx={{ mb: isAdmin ? 2 : 0 }}>
          <Grid item xs={12} sm={6}>
            <DatePicker
              label="Start Date"
              value={params.StartDate ? new Date(params.StartDate + 'T00:00:00') : null}
              onChange={handleStartDateChange}
              slotProps={{
                textField: { size: 'small', fullWidth: true },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DatePicker
              label="End Date"
              value={params.EndDate ? new Date(params.EndDate + 'T00:00:00') : null}
              onChange={handleEndDateChange}
              slotProps={{
                textField: { size: 'small', fullWidth: true },
              }}
            />
          </Grid>
        </Grid>
      </LocalizationProvider>

      {/* Admin-only selectors */}
      {isAdmin && (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              size="small"
              options={REPORT_TYPES as unknown as { Id: string; Title: string }[]}
              getOptionLabel={(option) => option.Title}
              value={selectedReportType as { Id: string; Title: string } | null}
              onChange={handleReportTypeChange}
              isOptionEqualToValue={(option, val) => option.Id === val.Id}
              renderInput={(inputProps) => (
                <TextField {...inputProps} label="Report Type" />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              size="small"
              options={consultantOptions}
              getOptionLabel={(option) => option.FullName}
              value={selectedConsultant}
              onChange={onConsultantSelect}
              onInputChange={(_e, value) => onConsultantSearch(value)}
              loading={consultantLoading}
              isOptionEqualToValue={(option, val) => option.Id === val.Id}
              filterOptions={(x) => x}
              renderInput={(inputProps) => (
                <TextField {...inputProps} label="Consultant" />
              )}
            />
          </Grid>
        </Grid>
      )}
    </Paper>
  );
}

export default PayrollFilters;
